import { useIntl } from "react-intl";
import { Link, graphql, useStaticQuery } from "gatsby";
import { LocalesKeys, defaultLocale, localesList } from "../../../locales";
import React, { useContext, useRef, useState } from "react";
import { useClickAway, useLockBodyScroll } from "react-use";
import ArrowRightIcon from "../../svgs/arrow-right-solid.svg";
import { LocaleContext } from "../../locales-utils";
import LogoSvg from "../../svgs/logo.svg";
import Sticky from "react-stickynode";
import { useLocation } from "@reach/router"

const getLinkByLocale = (
  links:
    | Queries.HeaderQuery["preHeader"]["nodes"]
    | Queries.HeaderQuery["header"]["nodes"],
  locale: LocalesKeys | string
) => {
  return links.find((link) => link.context?.locale === locale);
};

function getChangeLocaleLinks(
  defaultLink: Queries.PageDataQuery["defaultLink"],
  alternateLinks: Queries.PageDataQuery["alternateLinks"]["nodes"] = []
) {
  let links: Record<
    string,
    {
      title: string;
      path: string;
      disabled: boolean;
    }
  > = {};

  for (const locale of localesList) {
    links[locale] = {
      title: locale,
      path: locale === defaultLocale ? "/" : "/" + locale,
      disabled: false,
    };
  }

  for (const link of alternateLinks) {
    if (link.context?.locale) {
      links[link.context?.locale] = {
        title: link.context?.locale,
        path: link.path,
        disabled: false,
      };
    }
  }

  if (defaultLink?.context?.locale) {
    links[defaultLink?.context?.locale] = {
      title: defaultLink.context?.locale,
      path: defaultLink.path,
      disabled: true,
    };
  }

  return Object.values(links);
}

const Header = ({ siteTitle }: { siteTitle: string }) => {
  const data = useStaticQuery<Queries.HeaderQuery>(graphql`
    {
      site {
        siteMetadata {
          title
        }
      }
      news: allSitePage(
        filter: {
          context: {
            source: { eq: "pages" }
            name: { eq: "news" }
            pageNumber: { eq: 0 }
          }
        }
      ) {
        nodes {
          path
          context {
            locale
          }
          blunomyContent {
            title
          }
        }
      }
      expertise: allSitePage(
        filter: {
          context: { source: { eq: "pages" }, name: { eq: "our-expertise" } }
        }
      ) {
        nodes {
          path
          context {
            locale
          }
          blunomyContent {
            title
          }
        }
      }
      about: allSitePage(
        filter: {
          context: { source: { eq: "pages" }, name: { eq: "about-us" } }
        }
      ) {
        nodes {
          path
          context {
            locale
          }
          blunomyContent {
            title
          }
        }
      }
      publications: allSitePage(
        filter: {
          context: {
            source: { eq: "pages" }
            name: { eq: "publications" }
            pageNumber: { eq: 0 }
          }
        }
      ) {
        nodes {
          path
          context {
            locale
          }
          blunomyContent {
            title
          }
        }
      }
      sectors: allBlunomyContent(
      filter: { template: { eq: "sector" } }
      sort: {data: {order: ASC}}
      ) 
      {
        nodes {
          link {
            path
          }
          data {
            title
          }
        }
      }
    }
  `);

  const locale = useContext(LocaleContext);
  const intl = useIntl();
  const location = useLocation();


  const [openMenu, setOpenMenu] = useState(false);
  const [ddOpen, setDdOpen] = useState(false);
  const [langDropdown, setLangDropdown] = useState(false);
  const refLangDropdown = useRef(null);
  const refDdDropdown = useRef<HTMLDivElement>(null)

  useClickAway(refDdDropdown, () => {
   if (ddOpen) setDdOpen(false);
 });

  useClickAway(refLangDropdown, () => {
    setLangDropdown(false);
  });
  useLockBodyScroll(openMenu);

  const handleOpenMenu = () => {
    setOpenMenu(!openMenu);
  };
  const handleLangClick = () => {
    setLangDropdown(!langDropdown);
  };

  const ddLinks = data.sectors.nodes.map(node => ({
    title: node.data.title,
    path: node.link.path,
  }));

  const isDdActive = ddLinks.some(({ path }) =>
    location.pathname.startsWith(path)
  );

  const linksHeader = [
    { type: "normal", nodes: data.expertise.nodes },
    {
      type: "dropdown",
      title: intl.formatMessage({
        id: "header.ourSectors",
        defaultMessage: "Our sectors",
      }),
      items: ddLinks,
    },
    { type: "normal", nodes: data.about.nodes },
    { type: "normal", nodes: data.publications.nodes },
    { type: "normal", nodes: data.news.nodes },
  ].map(item => {
    if (item.type === "normal") {
      const link = getLinkByLocale(item.nodes, locale.currentLocale) as any;
      return {
        title: link?.blunomyContent?.title || "",
        path: link?.path,
      };
    } else {
      return {
        title: item.title,
        dropdown: item.items,
      };
    }
  });

  const links = getChangeLocaleLinks(
    locale?.defaultLink,
    locale?.alternateLinks
  );

  const renderMain = (
    <nav className={`Header-main 
    flex pt-[16px] pb-[12px] w-full 
    to-lg:order-1 to-lg:flex-col to-lg:flex-grow to-lg:px-6 
    lg:items-center lg:gap-[10px] lg:pt-[30px] lg:pb-[23px] lg:mx-auto lg:mt-0 lg:mb-0 lg:justify-between 
    xl:items-center xl:gap-[20px]
    `}>
      <div className={`flex items-center justify-between 
        to-lg:order-1 my-0 mx-0 
        lg:w-[175px] lg:mx-5
        xl:w-auto`}>
        <Link
          to="/"
          title={siteTitle}
          className="h-[25px] lg:h-auto"
          onClick={() => {
            setOpenMenu(false);
          }}
        >
          <LogoSvg className="max-w-full max-h-full" />
        </Link>
        <button
          className={`inline-flex relative gap-[8px] w-[30px] h-[30px] text-primary transition ease-out duration-200
            lg:hidden 
            before:absolute before:top-1/2 before:block before:bg-current before:rounded-[2px] before:h-[3px] before:origin-center
            after:absolute after:top-1/2 after:block after:bg-current after:rounded-[2px] after:h-[3px] after:origin-center
            before:w-full before:-translate-y-[5px]
            after:w-[75%] after:translate-y-[5px] after:right-0
            ${openMenu ? "is-open gap-0 before:w-full before:rotate-45 after:w-full  after:rotate-45" : ""}
            `}
          type="button"
          onClick={handleOpenMenu}
          title={
            openMenu
              ? intl.formatMessage({
                id: "header.closeMenu",
                defaultMessage: "Close menu",
              })
              : intl.formatMessage({
                id: "header.openMenu",
                defaultMessage: "Open menu",
              })
          }
        ></button>
      </div>
      <div className="lg:mx-5 lg:my-auto">
        {linksHeader.map((link) => {
          // ——— DROPDOWN ———
          if (link.dropdown) {
            return (
              <div
                key="our-sectors"
                ref={refDdDropdown}
                className={`
                  hidden my-6 to-lg:items-center to-lg:justify-between to-lg:order-2
                  lg:inline relative overflow-visible font-semibold text-primary
                  p-0 lg:mx-4 xl:mx-5
                  ${openMenu ? "to-lg:!flex" : ""}
                `}
              >
                <button
                  onClick={() => setDdOpen(!ddOpen)}
                  className={`
                    hidden relative inline-block leading-tight text-primary font-semibold text-[1.1rem]
                    pt-[3px] pb-[3px] px-0
                    to-lg:items-center to-lg:justify-between to-lg:order-2
                    lg:inline lg:py-[3px] lg:text-[1.125rem]
                    xl:text-[1.25rem]
                    lg:before:content-[''] lg:before:absolute lg:before:bottom-0
                    lg:before:left-1/2 lg:before:h-[3px] lg:before:bg-secondary
                    lg:before:origin-center lg:before:transform
                    lg:before:-translate-x-1/2 lg:before:w-0 lg:before:opacity-0
                    lg:before:transition-width lg:before:duration-150 lg:before:ease-out
                    lg:hover:before:w-full lg:hover:before:opacity-100
                    lg:focus-visible:before:w-full lg:focus-visible:before:opacity-100
                    lg:is-active:before:w-full lg:is-active:before:opacity-100
                    ${ddOpen ? "lg:before:w-full lg:before:opacity-100" : ""}
                    ${isDdActive
                    ? "lg:before:w-full lg:before:opacity-100"
                    : "lg:before:w-0 lg:before:opacity-0"}
                  `}
                >
                  {link.title}
                </button>

                {ddOpen && (
                  <ul className="to-lg:hidden absolute -left-4 mt-2 pb-2 bg-white shadow-md min-w-[200px] px-4 whitespace-nowrap">
                    {link.dropdown.map(item => (
                      <li key={item.path}>
                        <Link
                          to={item.path}
                          onClick={() => { setOpenMenu(false); setDdOpen(false); }}
                          className={`
                            inline-block mt-3 font-semibold text-primary pb-0.5
                            text-[1rem]
                            relative
                            lg:before:content-[''] lg:before:absolute lg:before:bottom-0
                            lg:before:left-1/2 lg:before:h-[3px] lg:before:bg-secondary
                            lg:before:origin-center lg:before:transform
                            lg:before:-translate-x-1/2 lg:before:w-0 lg:before:opacity-0
                            lg:before:transition-width lg:before:duration-150 lg:before:ease-out
                            lg:hover:before:w-full lg:hover:before:opacity-100
                            lg:focus-visible:before:w-full lg:focus-visible:before:opacity-100
                            lg:is-active:before:w-full lg:is-active:before:opacity-100
                          `}
                          activeClassName="lg:before:!w-full lg:before:!opacity-100"
                        >
                          {item.title}
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}

                <div className="lg:hidden w-full my-0">
                  <h3 className=" font-semibold text-primary text-[1.125rem]">
                    Our sectors
                  </h3>
                  <ul className="ml-4 space-y-1">
                    {data.sectors.nodes.map(item => (
                      <li key={item.link.path}>
                        <Link
                          to={item.link.path}
                          className={`
                            hidden relative text-primary font-semibold text-[1rem]
                            pt-3 px-0
                            to-lg:items-center to-lg:justify-between to-lg:order-2
                            
                            ${openMenu ? "to-lg:!flex" : ""}
                          `}
                          activeClassName="!font-bold"
                          onClick={() => setOpenMenu(false)}
                        >
                          {item.data.title}
                          <ArrowRightIcon className="w-4 relative top-[1px] lg:hidden shrink-0" />
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>

              </div>
            );
          }

          // ——— NORMAL LINKS ———
          return link.path && link.title ? (
            <Link
              key={link.path}
              to={link.path}
              className={`
                hidden relative inline-block leading-tight text-primary font-semibold text-[1.1rem]
                mt-6 mb-6 px-0
                to-lg:items-center to-lg:justify-between to-lg:order-2
                lg:inline lg:py-[3px] lg:mx-4 lg:text-[1.125rem]
                xl:text-[1.25rem] xl:mx-5
                lg:before:content-[''] lg:before:absolute lg:before:bottom-0
                lg:before:left-1/2 lg:before:h-[3px] lg:before:bg-secondary
                lg:before:origin-center lg:before:transform
                lg:before:-translate-x-1/2 lg:before:w-0 lg:before:opacity-0
                lg:before:transition-width lg:before:duration-150 lg:before:ease-out
                lg:hover:before:w-full lg:hover:before:opacity-100
                lg:focus-visible:before:w-full lg:focus-visible:before:opacity-100
                lg:is-active:before:w-full lg:is-active:before:opacity-100
                ${openMenu ? "to-lg:!flex" : ""}
              `}
              partiallyActive
              activeClassName="lg:before:!w-full lg:before:!opacity-100 to-lg:!font-bold"
              onClick={() => {
                setOpenMenu(false);
                setDdOpen(false);
              }}
            >
              {link.title}
              <ArrowRightIcon className="w-4 relative top-[1px] lg:hidden" />
            </Link>
          ) : null;
        })}

      </div>
    </nav>
  );

  return (
    <header className={`Header flex flex-col 
    to-lg:bg-white sticky to-lg:op-0 to-lg:z-[100] lg:relative 
    ${openMenu ? "is-open to-lg:!fixed to-lg:inset-0 to-lg:z-[100]" : ""}`}>
      <Sticky innerClass={`z-[50] shadow-[0_0_3px_0px_rgba(58,60,63,0.2)] 
      lg:px-10 
        ${openMenu ? "shadow-none" : ""}`} innerActiveClass="z-[50] bg-white">
        {renderMain}
      </Sticky>
    </header>
  );
};

export default Header;
